package com.swetdot.swetgram.data

import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import com.swetdot.swetgram.cache.SwetgramCache
import com.swetdot.swetgram.data.api.ApiClient
import com.swetdot.swetgram.data.cache.toCacheRow
import com.swetdot.swetgram.data.cache.toDb
import com.swetdot.swetgram.data.cache.toDomain
import com.swetdot.swetgram.data.cache.toCacheJson
import com.swetdot.swetgram.data.cache.toWaveformCacheJson
import com.swetdot.swetgram.domain.Chat
import com.swetdot.swetgram.domain.ChatId
import com.swetdot.swetgram.protocol.dto.Conversation
import com.swetdot.swetgram.protocol.dto.ConversationMute
import com.swetdot.swetgram.protocol.dto.ConversationPage
import com.swetdot.swetgram.protocol.dto.CreateDirectConversationRequest
import io.ktor.client.request.parameter
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.HttpMethod
import io.ktor.http.content.TextContent
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.withContext
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import kotlin.time.Instant

/**
 * The five mute lengths `muteConversation` accepts -- DECISIONS §5, and the contract's own enum
 * on the request body. SPS §15.3 is what asks for them ("Users should be able to mute: a specific
 * chat").
 *
 * [wireName] is the only spelling the server understands. There is no request DTO for this body in
 * `:protocol`, so the value is written into the JSON by hand (see [ChatRepository.mute]) rather
 * than declaring a wire type in `:shared`, which IMPLEMENTATION_NOTES §10 puts on the wrong side
 * of the boundary. `ChatCommands.deleteForMe` already makes the same trade for the same reason.
 *
 * TODO(layering): this and [MuteDeadline] are client DOMAIN types, not data-layer ones -- they
 * belong in `domain/Models.kt` beside `Chat`, which would stop `ui/chats` importing from `data` to
 * name a duration. They are here because `domain/` was not this change's to edit; the move is a
 * file move and an import change, and nothing else references them.
 */
enum class MuteDuration(val wireName: String) {
    ONE_HOUR("one_hour"),
    EIGHT_HOURS("eight_hours"),
    ONE_DAY("one_day"),
    ONE_WEEK("one_week"),
    FOREVER("forever"),
}

/**
 * When a mute ENDS, as far as this session knows -- and the client half of the one trap in the
 * feature.
 *
 * `conversation_members` stores a mute as a DEADLINE and derives `is_muted` from that column being
 * non-null, so NULL is how "not muted" is recorded and a `forever` mute has nowhere natural to go.
 * The server writes a far-future sentinel for it (`PushPolicy.MUTED_FOREVER_MS`, 9999-12-31) and
 * maps that back out before it reaches the wire. VERIFIED against the deployed server on
 * 2026-08-06:
 *
 * ```
 * PUT  /conversations/{id}/mute {"duration":"forever"}  -> 200 {"is_muted":true,"duration":"forever"}
 * GET  /conversations/{id}                             -> {"is_muted":true,"muted_until":null}
 * PUT  /conversations/{id}/mute {"duration":"one_hour"} -> 200 {..., "muted_until":"2026-08-06T00:45:44.575Z"}
 * ```
 *
 * So the sentinel does NOT reach this client today: a forever mute arrives as `is_muted` true with
 * `muted_until` absent, which is exactly [Indefinite]. [muteDeadlineOf] still floors the date,
 * because the cost of being wrong is a user being told their chat is muted until the year 9999,
 * and the guard is one comparison.
 */
sealed interface MuteDeadline {
    /** Muted, with no end the user can be shown. A `forever` mute, or a deadline beyond reason. */
    data object Indefinite : MuteDeadline

    /** Muted until [at], which is a real instant the user can act on. */
    data class Until(val at: Instant) : MuteDeadline
}

/**
 * A deadline this far out is not a date, it is a sentinel.
 *
 * Deliberately not the server's exact 9999-12-31: matching a magic number across the module
 * boundary would make this correct only for one implementation of it. Nothing a user can select
 * reaches even a year out -- the longest mute the contract offers is one week -- so anything past
 * the year 9000 is by construction "no end", however it was spelled.
 */
private val INDEFINITE_FLOOR: Instant = Instant.parse("9000-01-01T00:00:00Z")

/**
 * The mute state of one conversation, from the two fields the contract carries for it.
 *
 * Returns null for a conversation that is not muted. Note that `is_muted` is the ONLY authority on
 * whether a mute is in force: re-deriving it from `muted_until != null` here would report a forever
 * mute as unmuted, which is the exact defect the sentinel exists to prevent, and would also keep
 * reporting a LAPSED mute as active -- the server already stops both (`ConversationRepository
 * .toDto`).
 */
internal fun muteDeadlineOf(isMuted: Boolean, mutedUntil: String?): MuteDeadline? {
    if (!isMuted) return null
    val at = mutedUntil?.let { runCatching { Instant.parse(it) }.getOrNull() } ?: return MuteDeadline.Indefinite
    return if (at >= INDEFINITE_FLOOR) MuteDeadline.Indefinite else MuteDeadline.Until(at)
}

/**
 * Conversations, cache-first.
 *
 * The UI observes [observeChats] and never waits on the network. [refresh] writes into the same
 * cache, so a fetch updates whatever is on screen without the screen knowing a fetch happened.
 * That is what makes the list render instantly on cold start and keep working offline.
 */
class ChatRepository(
    private val api: ApiClient,
    private val cache: SwetgramCache,
) {
    private val queries = cache.cacheQueries

    /**
     * When each muted conversation's mute lapses, keyed by conversation id.
     *
     * IN MEMORY, and only for this session. The cache's `chats` table carries `isMuted` but no
     * deadline column, and the SQLDelight schema is not this change's to alter -- so the deadline
     * is held here, refreshed from the same `listConversations` page that refreshes everything
     * else, and rebuilt on the next launch. It is the same shape of overlay `ChatViewModel` keeps
     * for the live read watermark: derived state that a refresh reproduces, laid over cached truth.
     *
     * A conversation ABSENT from this map has no deadline known to this session. That is not the
     * same as "not muted" -- `Chat.isMuted` is the authority on that -- so a chat that is muted
     * and missing here degrades to the bell with no date, never to a wrong date.
     */
    private val _muteDeadlines = MutableStateFlow<Map<String, MuteDeadline>>(emptyMap())
    val muteDeadlines: StateFlow<Map<String, MuteDeadline>> = _muteDeadlines.asStateFlow()

    fun observeChats(archived: Boolean = false): Flow<List<Chat>> =
        queries.selectChats(archived.toDb())
            .asFlow()
            .mapToList(ioDispatcher)
            .map { rows ->
                rows.map { row ->
                    val members = queries.selectMembers(row.id).executeAsList().map { it.toDomain() }
                    val last = queries.selectLastMessage(row.id).executeAsOneOrNull()
                    val sender = last?.let { message ->
                        members.firstOrNull { it.id.value == message.senderId }
                            ?: queries.selectUser(message.senderId).executeAsOneOrNull()?.toDomain()
                    }
                    row.toDomain(
                        members = members,
                        // A message whose sender is not cached yet is dropped from the preview
                        // rather than rendered with a blank name.
                        lastMessage = if (last != null && sender != null) last.toDomain(sender) else null,
                    )
                }
            }

    /**
     * Pulls the conversation list and merges it into the cache.
     *
     * Deliberately returns Unit and throws on failure: the caller shows an error, but the list
     * on screen keeps whatever it had. A failed refresh must never blank the UI.
     */
    suspend fun refresh(archived: Boolean = false) {
        val page: ConversationPage = api.get("conversations") {
            parameter("archived", archived)
            parameter("limit", PAGE_SIZE)
        }

        // MERGED, never replaced. This runs twice per refresh -- once for the inbox and once for
        // the archive -- and assigning the whole map would let the second call erase the first
        // call's deadlines. Only conversations present in THIS page are touched.
        _muteDeadlines.update { current ->
            val next = current.toMutableMap()
            page.items.forEach { dto ->
                val deadline = muteDeadlineOf(dto.isMuted, dto.mutedUntil)
                if (deadline == null) next.remove(dto.id) else next[dto.id] = deadline
            }
            next
        }

        withContext(ioDispatcher) {
            queries.transaction {
                page.items.forEach { dto ->
                    val chat = dto.toDomain()

                    chat.members.forEach { member ->
                        val row = member.toCacheRow()
                        queries.upsertUser(
                            id = row.id,
                            username = row.username,
                            displayName = row.displayName,
                            avatarUrl = row.avatarUrl,
                            bio = row.bio,
                            location = row.location,
                            dateOfBirth = row.dateOfBirth,
                            showBirthYear = row.showBirthYear,
                            isOnline = row.isOnline,
                            lastSeenAt = row.lastSeenAt,
                            isDeleted = row.isDeleted,
                            hasBlackBadge = row.hasBlackBadge,
                        )
                        queries.replaceMembers(chatId = chat.id.value, userId = member.id.value)
                    }

                    queries.upsertChat(
                        id = chat.id.value,
                        kind = chat.kind.toDb(),
                        title = chat.title,
                        avatarUrl = chat.avatarUrl,
                        unreadCount = chat.unreadCount.toLong(),
                        isMuted = chat.isMuted.toDb(),
                        isArchived = chat.isArchived.toDb(),
                        isPinned = chat.isPinned.toDb(),
                        updatedAt = chat.updatedAt.toEpochMilliseconds(),
                        // Null on purpose: the upsert COALESCEs, so a chat-list refresh must
                        // not wipe a reconciliation cursor this endpoint knows nothing about.
                        changeCursor = null,
                    )

                    chat.lastMessage?.let { message ->
                        queries.upsertMessage(
                            chatId = message.chatId.value,
                            clientId = message.clientId,
                            serverId = message.id.value,
                            senderId = message.sender.id.value,
                            kind = message.kind.toDb(),
                            text = message.text,
                        imageUrl = message.imageUrl,
                        voiceUrl = message.voiceUrl,
                        voiceDurationMs = message.voiceDurationMs,
                        voiceWaveformJson = message.voiceWaveform.toWaveformCacheJson(),
                            replyToId = message.replyTo?.id?.value,
                            replyToSenderId = message.replyTo?.senderId?.value,
                            replyToSenderDisplayName = message.replyTo?.senderDisplayName,
                            replyToText = message.replyTo?.text,
                            replyToKind = message.replyTo?.kind?.toDb(),
                            state = message.state.toDb(),
                            sentAt = message.sentAt.toEpochMilliseconds(),
                            editedAt = message.editedAt?.toEpochMilliseconds(),
                            isDeleted = message.isDeleted.toDb(),
                            version = message.version.toLong(),
                            canEdit = message.canEdit.toDb(),
                            canDeleteForEveryone = message.canDeleteForEveryone.toDb(),
                            reactionsJson = message.reactions.toCacheJson(),
                            picsoId = message.picso?.id,
                            picsoExpiresAt = message.picso?.expiresAt,
                            idempotencyKey = null,
                            retryCount = 0,
                        )
                        // The sender of a last message need not be a member (they may have
                        // left), so cache them too or the preview loses its name.
                        val senderRow = message.sender.toCacheRow()
                        queries.upsertUser(
                            id = senderRow.id,
                            username = senderRow.username,
                            displayName = senderRow.displayName,
                            avatarUrl = senderRow.avatarUrl,
                            bio = senderRow.bio,
                            location = senderRow.location,
                            dateOfBirth = senderRow.dateOfBirth,
                            showBirthYear = senderRow.showBirthYear,
                            isOnline = senderRow.isOnline,
                            lastSeenAt = senderRow.lastSeenAt,
                            isDeleted = senderRow.isDeleted,
                            hasBlackBadge = senderRow.hasBlackBadge,
                        )
                    }
                }
            }
        }
    }

    /**
     * Removes one conversation from the cache, for a deletion the SERVER has already confirmed.
     *
     * [refresh] CANNOT SAY THIS, and that is the defect this closes. It merges a page in with
     * upserts and removes nothing, so a conversation the server has stopped returning -- which is
     * exactly what a completed `deleteConversation` produces, since `deleted_for_self_at` drops the
     * row out of `listConversations` -- kept its cached row for ever and [observeChats] kept drawing
     * it. The row was then STALE, and the next delete on it answered `404 conversation_not_found`,
     * which is the failure the user actually saw.
     *
     * Having [refresh] prune everything absent from the page instead is not available: the page is
     * [PAGE_SIZE] rows with `has_more`, so it would delete the tail of a long list that nothing ever
     * fetches back. The one conversation the user acted on is removed by id, here.
     *
     * The members and messages go with it. `createOrGetDirect` reopens the SAME conversation id
     * after clearing the caller's `deleted_for_self_at`, and the server's `min_visible_seq` means it
     * then serves none of the old history -- so rows left behind here would come back as a timeline
     * the server has already agreed to hide.
     */
    suspend fun forget(id: ChatId) {
        _muteDeadlines.update { it - id.value }
        withContext(ioDispatcher) {
            queries.transaction {
                queries.deleteChatMessages(id.value)
                queries.deleteChatMembers(id.value)
                queries.deleteChat(id.value)
            }
        }
    }

    /**
     * Opens the direct chat with [userId], or returns the existing one.
     *
     * The contract makes this idempotent by STATUS rather than by an idempotency key: 200 when
     * the conversation already existed, 201 when it was created. Both carry the same body, so
     * the client treats them identically.
     */
    suspend fun openDirectChat(userId: String): ChatId {
        val dto: Conversation = api.post("conversations/direct") {
            setBody(CreateDirectConversationRequest(userId))
        }
        val chatId = ChatId(dto.id)
        // THE CONVERSATION ALREADY EXISTS BY THIS POINT, so a failure here must not be reported as
        // one. The refresh is a SECOND request plus a cache write, and letting it throw discarded
        // the id the caller was waiting for -- so a network drop in the gap between the two, or a
        // failed local write, told the user the chat could not be opened when it had just been
        // created on the server. `createOrGetDirect` is idempotent by status, so the state was
        // recoverable, but only by a user who chose to try again after being told it had failed.
        //
        // The list reconciles on its next ordinary refresh either way, which is what makes dropping
        // this failure safe rather than merely convenient.
        try {
            refresh()
        } catch (cancellation: CancellationException) {
            // Not a failure of the refresh: the caller's scope is gone. It must propagate, or this
            // swallows a cancellation and the coroutine machinery loses track of it.
            throw cancellation
        } catch (_: Throwable) {
            // Deliberately dropped. See above.
        }
        return chatId
    }

    // ------------------------------------------------------------------------------------ mute
    //
    // SPS §15.3: "Users should be able to mute: a specific chat". Both operations have been
    // implemented, tested and deployed on the server since the messaging work landed, and until
    // now nothing in the app called either -- push already honoured a mute server-side
    // (`PushPolicy.decide`), so this is the control catching up with behaviour that was already
    // real. SDS §12.1's mute icon on the row was likewise drawing a state no user could set.

    /**
     * Mutes a conversation for one of the contract's five durations. `muteConversation`, `200`.
     *
     * Returns the deadline as the SERVER reported it, which is the value the caller should show:
     * `ConversationMute` carries `duration` as well as `muted_until`, so a `forever` mute is
     * unambiguous even before [muteDeadlineOf] looks at the date. The response is also folded into
     * [muteDeadlines] here rather than waiting for a refresh -- the caller refreshes for the row's
     * bell, and a deadline that appeared one request later would flicker.
     *
     * The body is a hand-built [TextContent] for the reason [MuteDuration] gives: there is no
     * request DTO for it in `:protocol`, and declaring one in `:shared` would put a wire type on
     * the wrong side of IMPLEMENTATION_NOTES §10.
     *
     * PUT, not POST, so it goes through [com.swetdot.swetgram.data.api.ApiClient.execute] directly
     * -- the client has no `put` helper, and adding one to a file this change does not own would be
     * a wider edit than the one method needs.
     */
    suspend fun mute(id: ChatId, duration: MuteDuration): MuteDeadline {
        val state: ConversationMute = api.execute(
            method = HttpMethod.Put,
            path = "conversations/${id.value}/mute",
            idempotencyKey = null,
            block = {
                setBody(
                    TextContent(
                        buildJsonObject { put("duration", duration.wireName) }.toString(),
                        ContentType.Application.Json,
                    ),
                )
            },
        )
        // `duration` is the server's own word for what it stored, so a forever mute is recognised
        // from the enum rather than from the absence of a date -- one fewer thing that has to be
        // true about the timestamp for the rendering to be right.
        val deadline = if (state.duration == MuteDuration.FOREVER.wireName) {
            MuteDeadline.Indefinite
        } else {
            muteDeadlineOf(state.isMuted, state.mutedUntil) ?: MuteDeadline.Indefinite
        }
        _muteDeadlines.update { it + (id.value to deadline) }
        return deadline
    }

    /** `unmuteConversation`, `204`. Clears the deadline as well as the flag. */
    suspend fun unmute(id: ChatId) {
        api.delete<Unit>("conversations/${id.value}/mute")
        _muteDeadlines.update { it - id.value }
    }

    private companion object {
        /** DECISIONS §5 sets 30 as the first-page size; the contract's default matches. */
        const val PAGE_SIZE = 30
    }
}
